import { useRef } from "react";
import { Download, History, Loader2, RotateCcw, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAccessToken } from "@/auth/useAccessToken";
import { useRestoreVersion, useUploadNewVersion, useVersions } from "@/api/hooks";
import type { DriveItem } from "@/api/driveApi";
import { downloadVersion } from "@/lib/download";
import { formatBytes, formatDateTime } from "@/lib/format";
import { useDialogs } from "./dialogs/DialogsProvider";
import { useToast } from "./toast/ToastProvider";

/**
 * OneDrive-style version history side-panel. Lists versions newest-
 * first, lets the user download any prior version, restore one (which
 * appends a new version pointing at the old bytes — the original is
 * never overwritten), or upload a new version of the file.
 */
export function VersionHistoryPanel({
    item,
    onClose
}: {
    item: DriveItem;
    onClose: () => void;
}) {
    const token = useAccessToken();
    const versions = useVersions(item.id);
    const restorer = useRestoreVersion();
    const uploader = useUploadNewVersion();
    const { confirm } = useDialogs();
    const toast = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUpload: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        try {
            await uploader.mutateAsync({ id: item.id, file: f });
            toast.success(`Uploaded a new version of “${item.name}”`);
        } catch (err) {
            toast.error(err);
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    // A one-off link the browser downloads natively ("name (vN).ext"), with
    // its own progress UI — nothing is buffered in the page.
    const handleDownload = async (versionId: string, versionNumber: number) => {
        try {
            await downloadVersion(item.id, versionId, token);
        } catch (err) {
            toast.error(err instanceof Error ? new Error(`Couldn't download version ${versionNumber}: ${err.message}`) : err);
        }
    };

    const handleRestore = async (versionId: string, versionNumber: number) => {
        const ok = await confirm({
            title: `Restore version ${versionNumber}?`,
            message: "A new version is created with the old contents. Nothing is overwritten.",
            confirmLabel: "Restore"
        });
        if (!ok) return;
        try {
            await restorer.mutateAsync({ id: item.id, versionId });
            toast.success(`Restored version ${versionNumber}`);
        } catch (err) {
            toast.error(err);
        }
    };

    return (
        <aside
            role="dialog"
            aria-label={`Version history for ${item.name}`}
            data-drive-overlay=""
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") onClose();
            }}
            className="drive-slide-left-mobile fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col border-l bg-background shadow-2xl"
        >
            <header className="flex h-14 items-center gap-2 border-b px-3">
                <History className="h-4 w-4 text-primary" aria-hidden />
                <div className="min-w-0 flex-1">
                    <h2 className="truncate text-sm font-semibold">Version history</h2>
                    <p className="truncate text-xs text-muted-foreground">{item.name}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close version history" autoFocus>
                    <X className="h-4 w-4" />
                </Button>
            </header>

            <div className="border-b p-3">
                <input ref={fileInputRef} type="file" hidden onChange={handleUpload} />
                <Button onClick={() => fileInputRef.current?.click()} disabled={uploader.isPending} className="w-full" size="sm">
                    {uploader.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploader.isPending ? "Uploading…" : "Upload new version"}
                </Button>
                <p className="mt-2 text-xs text-muted-foreground">
                    Replaces the current contents while keeping every prior version downloadable.
                </p>
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
                {versions.isPending ? (
                    <p className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                    </p>
                ) : versions.isError ? (
                    <p className="p-4 text-sm text-destructive">{versions.error.message}</p>
                ) : !versions.data || versions.data.length === 0 ? (
                    <p className="p-4 text-sm text-muted-foreground">No versions yet.</p>
                ) : (
                    <ul role="list" className="divide-y">
                        {versions.data.map((v, idx) => (
                            <li key={v.id} className="flex flex-col gap-1 px-3 py-2.5 text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium">Version {v.versionNumber}</span>
                                    {idx === 0 ? (
                                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                                            Current
                                        </span>
                                    ) : null}
                                    <span className="ml-auto text-xs text-muted-foreground">{formatBytes(v.sizeBytes)}</span>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {formatDateTime(v.createdAt)} · {v.createdBy}
                                </div>
                                <div className="mt-1 flex gap-1">
                                    <Button variant="outline" size="sm" onClick={() => void handleDownload(v.id, v.versionNumber)}>
                                        <Download className="h-3.5 w-3.5" />
                                        Download
                                    </Button>
                                    {idx !== 0 ? (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => void handleRestore(v.id, v.versionNumber)}
                                            disabled={restorer.isPending}
                                        >
                                            <RotateCcw className="h-3.5 w-3.5" />
                                            Restore
                                        </Button>
                                    ) : null}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </aside>
    );
}
